import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	useDataInputDialog,
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { useOpenEditHubDialog } from "./useOpenEditHubDialog";
import { never } from "client_legacy/ndaw/errors";
import { ChooseHubTemplateDialog } from "../Templates/ChooseHubTemplateDialog";
import { isNotNil } from "client/Misc/utils";

export type CreateHubItemDialogProps = {
	kind: HubItemKind;
	preferredCategoryId?: number | null;
	onClose(wasAnyCategoryCreated?: boolean): void;
	onSaveHubItem?(): void;
	onBack?(wasAnyCategoryCreated?: boolean): void;
};

export const CreateHubItemDialog = (props: CreateHubItemDialogProps): ReactElement => {
	const {
		kind,
		preferredCategoryId = null,
		onClose,
		onSaveHubItem,
		onBack
	} = props;

	const [wasAnyCategoryCreated, setWasAnyCategoryCreated] = useState<boolean>(false);
	const [openedEditHubDialog, openEditHubDialog] = useOpenEditHubDialog();

	const openEditHubItemHandler = useCallback(
		(selectedTemplateId?: number): void => {
			openEditHubDialog({
				kind,
				selectedTemplateId,
				preferredCategoryId,
				onClose,
				onAnyCategoryCreatedChange: setWasAnyCategoryCreated,
				onSave: onSaveHubItem
			});
		},
		[
			kind,
			onClose,
			onSaveHubItem,
			openEditHubDialog,
			preferredCategoryId,
		]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			return onBack
				? () => onBack(wasAnyCategoryCreated)
				: undefined;
		},
		[onBack, wasAnyCategoryCreated]
	);

	if (openedEditHubDialog) {
		return openedEditHubDialog;
	}

	return (
		<ChooseHubTemplateDialog
			kind={kind}
			onChoose={openEditHubItemHandler}
			onClose={() => onClose(wasAnyCategoryCreated)}
			onBack={backHandler}
		/>
	);
};
CreateHubItemDialog.displayName = "CreateHubItemDialog";

export type CreateHubItemDialogInitialData = Omit<CreateHubItemDialogProps, "onClose" | "onSave">
& (
	| {
		closeDialogOn?: "close";
		onClose?: undefined;
	}
	| {
		closeDialogOn: "back";
		onClose(): void;
	}
);

export function useCreateHubItemDialog(): [
	element: ReactElement | null,
	open: (props: CreateHubItemDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(data, onClose, onSave) => {
			const {
				closeDialogOn,
				onBack,
				onClose: onCloseFromProps,
				...props
			} = data;

			const closeHandler = (): void => {
				switch (closeDialogOn) {
					case "close":
					case undefined:
						onClose();
						break;
					case "back":
						onCloseFromProps();
						break;
					default:
						never(closeDialogOn);
				}
			};

			const backHandler = isNotNil(onBack) || closeDialogOn === "back"
				? (): void => {
					if (closeDialogOn === "back") {
						onClose();
					}

					onBack?.();
				}
				: undefined;

			return (
				<CreateHubItemDialog
					{...props}
					onClose={closeHandler}
					onBack={backHandler}
					onSaveHubItem={onSave}
				/>
			);
		}
	);
}